import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiBriefcase, FiClock, FiMic, FiPlay, FiArrowRight, FiAlertCircle, FiInfo } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { applicationApi } from '../../services/api';
import { DisabledCreditButton } from '../../components/membership';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import { useBalance } from '../../contexts/BalanceContext';

const MockInterview = () => {
  const navigate = useNavigate();
  const { deductCredits, refreshBalance, SERVICE_COSTS } = useBalance();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [generatingInterview, setGeneratingInterview] = useState(false);
  const [interviewType, setInterviewType] = useState('application'); // 'application' or 'general'

  // Standard questions for general interview
  const standardQuestions = [
    {
      id: 'std-1',
      question: 'Tell me about yourself and your background.',
      suggested_answer: 'Start with your current role, then highlight relevant experience, skills, and achievements that relate to the position. Keep it concise (1-2 minutes) and focus on professional rather than personal details.'
    },
    {
      id: 'std-2',
      question: 'What are your greatest strengths?',
      suggested_answer: "Choose 2-3 strengths that are relevant to the job. For each strength, provide a specific example that demonstrates how you've used it in a professional setting. Focus on qualities that add value to the organization."
    },
    {
      id: 'std-3',
      question: 'What is your greatest weakness?',
      suggested_answer: "Choose a genuine weakness that isn't critical to the job, explain how you're working to improve it, and share specific steps you're taking. Show self-awareness and a commitment to professional growth."
    },
    {
      id: 'std-4',
      question: "Why are you interested in this position?",
      suggested_answer: "Research the company and position beforehand. Discuss specific aspects of the role that appeal to you, how it aligns with your career goals, and what attracts you to the company's mission, culture, or products/services."
    },
    {
      id: 'std-5',
      question: 'Describe a challenging situation at work and how you handled it.',
      suggested_answer: 'Use the STAR method (Situation, Task, Action, Result). Describe a specific challenge, your role, the actions you took to address it, and the positive outcome. Focus on demonstrating problem-solving skills and resilience.'
    }
  ];

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationApi.getAllApplications();
      // Filter applications to only include those in 'applied' or 'interviewing' status
      const activeApplications = response.data.filter(
        app => app.status === 'applied' || app.status === 'interviewing'
      );
      setApplications(activeApplications);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleStartMockInterview = async () => {
    if (interviewType === 'general') {
      // Use standard questions for general interview (free)
      navigate(`/jobs/mock-interview/session/general`, {
        state: { 
          application: {
            id: 'general',
            company: 'General Practice',
            jobTitle: 'Interview Practice',
            status: 'practice'
          },
          questions: standardQuestions
        }
      });
      return;
    }
    
    if (!selectedApplication) {
      toast.error('Please select a job application');
      return;
    }

    try {
      setGeneratingInterview(true);
      
      // Generate interview questions using the existing API
      const response = await applicationApi.generateInterviewQuestions(selectedApplication.id);
      
      // Log the response to help with debugging
      
      // Handle the specific response format: { success: true, data: [...] }
      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
        // Transform the data to match our expected format
        const formattedQuestions = response.data.data.map(item => ({
          id: Math.random().toString(36).substring(2, 11), // Generate a random ID
          question: item.question,
          suggested_answer: item.answer || item.tips || '',
          tips: item.tips
        }));
        
        // Update credit balance immediately after successful API call
        const creditCost = SERVICE_COSTS['interview_question'] || 5;
        deductCredits(creditCost);
        
        // Refresh the actual balance from the server to ensure UI is updated
        refreshBalance().then(updatedBalance => {
        });
        
        // Show success message about credits
        toast.success('5 credits used for this mock interview');
        
        // Navigate to the interview session with the questions
        navigate(`/jobs/mock-interview/session/${selectedApplication.id}`, {
          state: { 
            application: selectedApplication,
            questions: formattedQuestions
          }
        });
      } else {
        console.error('Invalid questions format:', response.data);
        toast.error('Failed to generate interview questions');
      }
    } catch (error) {
      console.error('Error generating interview questions:', error);
      // Check if error is due to insufficient credits
      if (error.response && error.response.status === 402) {
        toast.error('Not enough credits. Please upgrade your plan or purchase more credits.');
      } else {
        toast.error('Failed to generate interview questions');
      }
    } finally {
      setGeneratingInterview(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading applications..." />;
  }

  const copy = pageDescriptions['/jobs/mock-interview'];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <PageHeader
        title={copy.title}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <button
            onClick={() => navigate('/applications')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Back to Applications
          </button>
        }
      />

      {applications.length === 0 ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FiAlertCircle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You don't have any active job applications. 
                <Link to="/jobs" className="font-medium underline text-yellow-700 hover:text-yellow-600 ml-1">
                  Apply for jobs
                </Link> to get tailored interview questions, or try our general interview practice below.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FiInfo className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Try the free general interview practice first to get comfortable with the format before using credits on job-specific interviews.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card overflow-hidden mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            Prepare for Your Interview
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Choose an interview type to practice your skills.
          </p>
        </div>

        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interview Type
            </label>
            <div className="flex space-x-4">
              <div className="flex items-center">
                <input
                  id="application-interview"
                  name="interview-type"
                  type="radio"
                  checked={interviewType === 'application'}
                  onChange={() => setInterviewType('application')}
                  className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
                />
                <label htmlFor="application-interview" className="ml-2 block text-sm text-gray-700">
                  Job-specific Interview
                </label>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  5 Credits
                </span>
              </div>
              <div className="flex items-center">
                <input
                  id="general-interview"
                  name="interview-type"
                  type="radio"
                  checked={interviewType === 'general'}
                  onChange={() => setInterviewType('general')}
                  className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
                />
                <label htmlFor="general-interview" className="ml-2 block text-sm text-gray-700">
                  General Interview Practice
                </label>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Free
                </span>
              </div>
            </div>
          </div>

          {interviewType === 'application' && (
            <div className="mb-6">
              <div className="flex items-center">
                <label htmlFor="application" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Job Application
                </label>
                <div className="relative ml-2 group">
                  <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
                  <div className="absolute z-10 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-64 bottom-full left-1/2 transform -translate-x-1/2 mb-1">
                    Job-specific mock interviews cost 5 credits. Try the general interview practice for free if you want to practice first.
                  </div>
                </div>
              </div>
              <select
                id="application"
                name="application"
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                value={selectedApplication ? selectedApplication.id : ''}
                onChange={(e) => {
                  const appId = e.target.value;
                  const app = applications.find(a => a.id === appId);
                  setSelectedApplication(app || null);
                }}
                disabled={applications.length === 0}
              >
                <option value="">-- Select a job application --</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.company} - {app.jobTitle}
                  </option>
                ))}
              </select>
              {applications.length === 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  No active applications available. Try general interview practice instead.
                </p>
              )}
            </div>
          )}

          {interviewType === 'application' && selectedApplication && (
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h3 className="text-md font-medium text-gray-900 mb-2">
                {selectedApplication.company} - {selectedApplication.jobTitle}
              </h3>
              <div className="flex items-center text-sm text-gray-500 mb-2">
                <FiBriefcase className="mr-1" />
                <span>Status: {selectedApplication.status}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <FiClock className="mr-1" />
                <span>Applied: {new Date(selectedApplication.appliedAt).toLocaleDateString()}</span>
              </div>
            </div>
          )}

          {interviewType === 'general' && (
            <div className="bg-blue-50 p-4 rounded-md mb-6">
              <div className="flex items-center mb-2">
                <h3 className="text-md font-medium text-blue-900">
                  General Interview Practice
                </h3>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Free
                </span>
              </div>
              <p className="text-sm text-blue-700">
                Practice with standard interview questions that are commonly asked across different industries and positions. This option doesn't use any credits.
              </p>
            </div>
          )}

          <div className="flex items-center justify-center mt-6">
            {interviewType === 'application' ? (
              <DisabledCreditButton
                serviceType="interview_question"
                onClick={handleStartMockInterview}
                disabled={(interviewType === 'application' && !selectedApplication) || generatingInterview}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {generatingInterview ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Generating Interview...
                  </>
                ) : (
                  <>
                    <FiMic className="mr-2" />
                    Start Mock Interview (5 Credits)
                  </>
                )}
              </DisabledCreditButton>
            ) : (
              <button
                type="button"
                onClick={handleStartMockInterview}
                disabled={generatingInterview}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                {generatingInterview ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Generating Interview...
                  </>
                ) : (
                  <>
                    <FiMic className="mr-2" />
                    Start Mock Interview
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            How It Works
          </h2>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 p-3 rounded-full mb-3">
                <FiBriefcase className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-md font-medium text-gray-900 mb-2">1. Select a Job</h3>
              <p className="text-sm text-gray-500">
                Choose from your active job applications or try general practice questions.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 p-3 rounded-full mb-3">
                <FiMic className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-md font-medium text-gray-900 mb-2">2. Practice Answers</h3>
              <p className="text-sm text-gray-500">
                Record your responses to each question with a 1-minute time limit.
              </p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-100 p-3 rounded-full mb-3">
                <FiPlay className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="text-md font-medium text-gray-900 mb-2">3. Review & Improve</h3>
              <p className="text-sm text-gray-500">
                Listen to your recordings and compare with suggested answers to improve.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockInterview;